import {
  Email,
  GenerateVerificationCodePayload,
  Phone,
  ProfilePost,
  ProfileResponse,
  ProfileSelectorType,
  ProfileSlice,
  VerifyCodePayload,
} from './ProfileTypes'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { Logger } from '@meprism/app-utils'
import { APIInterface, createBaseAsyncThunk } from '../base'
import { AuthenticationState } from '../authentication/authenticationTypes'
import { ProfileFields } from '../../schemas/EmployeeProfileForm'
import { ExecutiveCorporateEmployee } from '@meprism/portal/src/redux/corporateService'

export interface BaseProfileState {
  authentication: AuthenticationState
  profile: ProfileSelectorType
}

const postProfileRequest = async (
  profile: ProfilePost,
  muid: string,
  API: APIInterface,
): Promise<ProfileResponse> => {
  return API.post('Corporate', `/user-profile?muid=${muid}`, {
    body: profile,
  })
}

interface AdminUserSetupPayload {
  profiles: any[]
  code: string
}

interface PostAdminUserSetupResponse {
  error?: {
    error: string
    [key: string]: any
  }
  created_users?: string[]
  existing_users?: string[]
}

const getExecutiveUsers = async (
  code: string,
  API: APIInterface,
): Promise<ExecutiveCorporateEmployee[]> => {
  return await API.get('Profile', `/employee-users-list/${code}`, {})
}

export const FetchExecutiveUsers = createBaseAsyncThunk(
  'FetchExecutiveUsers',
  async (code: string, { extra }) => {
    try {
      return await getExecutiveUsers(code, extra.API)
    } catch (error) {
      Logger.error(`Error getting executive users code: ${code}: ${error}`)
      throw error
    }
  },
)
const postAdminUserSetupRequest = async (
  payload: AdminUserSetupPayload,
  API: APIInterface,
): Promise<PostAdminUserSetupResponse> => {
  return await API.post('Profile', '/admin-user-setup', { body: payload })
}

export const PostAdminUserSetup = createBaseAsyncThunk(
  'postAdminUserSetup',
  async (payload: AdminUserSetupPayload, { extra }) => {
    const { Toast } = extra
    let message = ''
    let message2 = ''
    try {
      const res = await postAdminUserSetupRequest(payload, extra.API)
      const error = res.error?.error || ''

      const created = res.created_users || []
      const existing = res.existing_users || []

      if (created.length > 0) {
        message += `Created ${created.length} user${
          created.length > 1 ? 's' : ''
        }: ${created.join(', ')}`
      }

      if (existing.length > 0) {
        message2 += `Existing ${existing.length} user${
          existing.length > 1 ? 's were' : ' was'
        } skipped: ${existing.join(', ')}`
      }

      Toast.show({
        type: created.length > 0 ? 'success' : 'error',
        text1: message || '',
        text2: message2 || '',
      })

      return res
    } catch (error) {
      const errorMsg = error.response?.data?.error?.error
      Logger.error(
        `Error creating executive admin users : ${JSON.stringify(
          error.response?.data,
        )}`,
      )
      if (errorMsg === 'CodeLimitReached') {
        if (error.response?.data?.created_users?.length != 0) {
          message = `Created ${
            error.response?.data?.created_users?.length
          } user(s): ${error.response?.data?.created_users?.join(', ')}`
        }
        if (error.response?.data?.existing_users?.length != 0) {
          message2 = `Skippped ${
            error.response?.data?.existing_users?.length
          } existing user(s) : ${error.response?.data?.existing_users?.join(
            ', ',
          )}`
        }
        Toast.show({
          type: 'error',
          text1: `We couldn't add all of the users because you have reached your seat limit. Please contact support@meprism.com for help.`,
          text2: message && message2 ? `${message}\n${message2}` : message || message2 || '',
        })
        return
      }
      if (error.response?.data?.existing_users?.length != 0) {
        message = `Skippped ${
          error.response?.data?.existing_users?.length
        } existing user(s) : ${error.response?.data?.existing_users?.join(
          ', ',
        )}`
      }
      Toast.show({
        type: 'error',
        text1: `We could not create users`,
        text2: message ? message : 'Please try again later',
      })
    }
  },
)

interface birthdateUpdateRequest {
  muid: string
  birthdate: string
}

const postExecutiveUserBirthdate = async (
  payload: birthdateUpdateRequest,
  API: APIInterface,
): Promise<PostAdminUserSetupResponse> => {
  const update_payload = { birthdate: payload.birthdate }
  return await API.post(
    'Corporate',
    `/user-profile/update-birthdate?muid=${payload.muid}`,
    { body: update_payload },
  )
}

export const UpdateExecutiveUserBirthdate = createBaseAsyncThunk(
  'postAdminUserBirthdate',
  async (payload: birthdateUpdateRequest, { extra }) => {
    const { Toast } = extra
    try {
      const res = await postExecutiveUserBirthdate(payload, extra.API)

      Toast.show({
        type: 'success',
        text1: 'Birthdate was updated successfully',
      })

      return res
    } catch (error) {
      Toast.show({
        type: 'error',
        text1: 'We could not update birthdate, Try Again Later',
      })
      throw error
    }
  },
)


export const FetchProfile = createBaseAsyncThunk(
  'fetchProfile',
  async (_, { getState, extra }) => {
    try {
      const state = getState() as BaseProfileState
      const muid = state.authentication?.muid
      const payload: ProfileResponse = await extra.API.get('Corporate', `/user-profile?muid=${muid}`, {})
      // For now, manually ensure that things that should be arrays in fact are,
      // because I guess they can come back nullish or not arrayed...
      // @TODO: Remove this logic once data model hardens a little
      const prof: ProfileSlice = {
        ...payload,
        names: {
          first: Array.isArray(payload.names?.first)
            ? payload.names.first
            : [payload?.name?.first ?? ''],
          middle: Array.isArray(payload.names?.middle)
            ? payload.names.middle
            : [payload?.name?.middle ?? ''],
          last: Array.isArray(payload.names?.last)
            ? payload.names.last
            : [payload?.name?.last ?? ''],
        },
        address: Array.isArray(payload.address)
          ? payload.address
          : [payload.address],
        metadata: payload.metadata ?? { dob_counter: 0 },
      }
      return prof
    } catch (error) {
      Logger.error(`Error getting profile: ${error}`)
      throw error
    }
  },
)

export const FetchExecutiveEmployeeProfile = createBaseAsyncThunk(
  'fetchProfile',
  async (muid: string, { extra }) => {
    try {
      const res = await extra.API.get('Corporate', `/user-profile?muid=${muid}`, {})
      return res
    } catch (error) {
      Logger.error(`Error getting profile: ${error}`)
      throw error
    }
  },
)

export const UpdateExecutiveEmployeeProfile = createBaseAsyncThunk(
  'UpdateProfile',
  async (
    { payload, muid }: { payload: ProfileFields; muid: string },
    { extra },
  ) => {
    try {
      const updated_payload = { ...payload, userID: muid }
      const res = await extra.API.post('Corporate', `/user-profile?muid=${muid}`, {
        body: updated_payload,
      })
      extra.Toast.show({
        type: 'success',
        text1: 'Profile has been updated successfully',
      })
      return res
    } catch (error) {
      extra.Toast.show({
        type: 'error',
        text1: 'Profile could not be updated. Try again later.',
      })
      Logger.error(`Error updating profile: ${error}`)
      throw error
    }
  },
)

export const PostProfile = createBaseAsyncThunk(
  'postProfile',
  async (payload: ProfileFields, { getState, extra }) => {
    const state = getState() as BaseProfileState
    const muid = state.authentication?.muid
    try {
      const response = await postProfileRequest(
        {
          ...payload,
          userID: muid as string,
          name: {
            first: payload.names?.first?.[0]?.trim?.() ?? '',
            middle: payload.names?.middle?.[0]?.trim?.() ?? '',
            last: payload.names?.last?.[0]?.trim?.() ?? '',
          },
        },
        muid as string,
        extra.API,
      )
      extra.AnalyticsManager.trackTypedEvent({
        event: "Profile Updated - Portal",
        source: 'Business Portal'
        }
      )
      extra.Toast.show({
        type: 'success',
        text1: 'Your profile has been saved',
      })
      const prof: ProfileSlice = {
        ...response,
        names: {
          first: Array.isArray(response.names?.first)
            ? response.names.first
            : [response?.name?.first ?? ''],
          middle: Array.isArray(response.names?.middle)
            ? response.names.middle
            : [response?.name?.middle ?? ''],
          last: Array.isArray(response.names?.last)
            ? response.names.last
            : [response?.name?.last ?? ''],
        },
        address: Array.isArray(response.address)
          ? response.address
          : [response.address],
        metadata: response.metadata ?? { dob_counter: 0 },
      }
      return prof
    } catch (error) {
      Logger.error(`Error posting profile: ${error} ${JSON.stringify(error)}`)
      extra.Toast.show({
        type: 'error',
        text1: 'We were unable to save your profile',
        text2:
          'Please ensure all required fields are correctly filled out. Otherwise, please contact support.',
      })
      throw error
    }
  },
)

export const GenerateVerificationCode = createBaseAsyncThunk(
  'generateCode',
  async (payload: GenerateVerificationCodePayload, { extra }) => {
    try {
      await extra.API.post('Profile', '/generate', { body: payload })
    } catch (error) {
      Logger.error(`Error generating verification code: ${error}`)
      throw error
    }
  },
)

export const VerifyCode = createBaseAsyncThunk(
  'veriifyCode',
  async (payload: VerifyCodePayload, { extra }) => {
    try {
      await extra.API.post('Profile', '/verify', { body: payload })
    } catch (error) {
      Logger.error(`Error validating verification code: ${error}`)
      throw error
    }
  },
)

const initialState: ProfileSelectorType = {
  profile: {
    name: {
      first: '',
      middle: '',
      last: '',
    },
    names: {
      first: [''],
      middle: [''],
      last: [''],
    },
    address: [
      {
        street1: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      },
    ],
    emails: [{ address: '', validated: false }] as Email[],
    phones: [{ number: '', validated: false }] as Phone[],
    gender: '',
    birthdate: '',
    accountAge: undefined,
    dataUsage: 0,
    userID: '',
    employer: '',
    jobTitle: '',
    industry: '',
    mp_auth_letter_signature: '',
    auth_letter_version: undefined,
  },
  error: false,
  meta: {
    blockingRequestIds: [],
    profileFetchIds: [],
  },
}

const { actions, reducer } = createSlice({
  name: 'profile',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(FetchProfile.fulfilled, (state, { payload, meta }) => {
      state.error = false
      state.profile = payload
      state.meta.profileFetchIds = state.meta.profileFetchIds.filter(
        (id) => id !== meta.requestId,
      )
    })
    builder.addCase(FetchProfile.pending, (state, { meta }) => {
      state.meta.profileFetchIds.push(meta.requestId)
      state.error = false
    })
    builder.addCase(FetchProfile.rejected, (state, { meta }) => {
      state.meta.profileFetchIds = state.meta.profileFetchIds.filter(
        (id) => id !== meta.requestId,
      )
      state.error = true
    })
    builder.addCase(PostProfile.fulfilled, (state, { payload, meta }) => {
      state.meta.blockingRequestIds = state.meta.blockingRequestIds.filter(
        (id) => id !== meta.requestId,
      )
      state.error = false
      state.profile = payload
    })
    builder.addCase(PostProfile.pending, (state, { meta }) => {
      state.meta.blockingRequestIds.push(meta.requestId)
      state.error = false
    })
    builder.addCase(PostProfile.rejected, (state, { meta }) => {
      state.meta.blockingRequestIds = state.meta.blockingRequestIds.filter(
        (id) => id !== meta.requestId,
      )
      state.error = true
    })
    builder.addCase(GenerateVerificationCode.fulfilled, (state, { meta }) => {
      state.meta.blockingRequestIds = state.meta.blockingRequestIds.filter(
        (id) => id !== meta.requestId,
      )
      state.error = false
    })
    builder.addCase(GenerateVerificationCode.pending, (state, { meta }) => {
      state.meta.blockingRequestIds.push(meta.requestId)
      state.error = false
    })
    builder.addCase(GenerateVerificationCode.rejected, (state, { meta }) => {
      state.meta.blockingRequestIds = state.meta.blockingRequestIds.filter(
        (id) => id !== meta.requestId,
      )
      state.error = true
    })
    builder.addCase(VerifyCode.fulfilled, (state, { meta }) => {
      state.meta.blockingRequestIds = state.meta.blockingRequestIds.filter(
        (id) => id !== meta.requestId,
      )
      state.error = false
    })
    builder.addCase(VerifyCode.pending, (state, { meta }) => {
      state.meta.blockingRequestIds.push(meta.requestId)
      state.error = false
    })
    builder.addCase(VerifyCode.rejected, (state, { meta }) => {
      state.meta.blockingRequestIds = state.meta.blockingRequestIds.filter(
        (id) => id !== meta.requestId,
      )
      state.error = true
    })
  },
})

export const selectProfileSlice = (state: BaseProfileState) => state.profile

export const selectProfile = (state: BaseProfileState) => state.profile.profile

// The "modern" profile only uses SOME fields. Rather than have to deal with updating the whole world
// (not to mention that the backend probably still has this legacy cruft)
// easiest to just select the fields we WILL use for privacy profile updates
// Primary use case is asking ex. "Are there unsaved changes to this profile"
// without having to examine stuff we don't even use like jobTitle
export const selectProfileFields = createSelector(
  [selectProfile],
  ({
    names,
    emails,
    birthdate,
    phones,
    address,
    mp_auth_letter_signature,
    gender,
    auth_letter_version,
    metadata
  }) => ({
    names,
    emails,
    birthdate,
    phones,
    address,
    mp_auth_letter_signature,
    gender,
    auth_letter_version,
    metadata
  }),
)

export const selectProfileName = createSelector(
  selectProfileFields,
  ({ names }) =>
    [names?.first?.[0], names?.middle?.[0], names?.last?.[0]]
      .filter(Boolean)
      .join(' '),
)

// OK this is not the best name, but this one is for us POSTing a profile
export const selectProfileLoading = (state: BaseProfileState) =>
  state.profile.meta.blockingRequestIds.length > 0
// ...and this one is for us fetching, which we do a lot more frequently
export const selectProfileFetching = (state: BaseProfileState) =>
  state.profile.meta.profileFetchIds.length > 0

export const selectShouldShowAuthorizationModal = createSelector(
  [
    selectProfileFetching,
    (state: BaseProfileState) =>
      !!state.profile?.profile?.mp_auth_letter_signature,
  ],
  (isFetching, hasSignature) => !isFetching && !hasSignature,
)

export const ProfileActions = actions
export const ProfileReducer = reducer

export type ProfileFields = ReturnType<typeof selectProfileFields>

export const ProfileSelectors = {
  selectProfileSlice,
  selectProfile,
  selectProfileFields,
  selectProfileLoading,
  selectProfileFetching,
  selectProfileName,
} as const

export const CURRENT_AUTH_LETTER_VERSION = '1.0.0'
